/** True on phones/tablets (touch-primary devices). */
export function isCoarsePointer() {
  if (typeof window === 'undefined') return false;
  return window.matchMedia?.('(pointer: coarse)').matches ?? false;
}

/**
 * True when player-search UI should use a keyboard-aware bottom sheet (phones + narrow viewports).
 * Narrow width covers iOS/desktop edge cases where pointer: fine is still reported on touch devices.
 */
export function isMobileSearchSheet() {
  if (typeof window === 'undefined') return false;
  const narrow = window.matchMedia?.('(max-width: 639px)').matches ?? false;
  return narrow || isCoarsePointer();
}

/**
 * Shared attributes that reduce mobile keyboard / autocomplete quirks.
 * Prefer type="text" with inputMode="search" — iOS `type="search"` can defer/filter oddly.
 */
export const mobileSearchInputAttrs = {
  autoComplete: 'off',
  autoCorrect: 'off',
  autoCapitalize: 'none',
  spellCheck: false,
  enterKeyHint: 'search',
  inputMode: 'search',
};

/**
 * Re-focus only when iOS Safari loses focus after a parent re-render — not on every keystroke.
 */
export function refocusMobileSearchInput(inputRef) {
  if (typeof window === 'undefined') return;
  if (!isCoarsePointer()) return;
  const el = inputRef.current;
  if (!el || document.activeElement === el) return;

  requestAnimationFrame(() => {
    if (document.activeElement !== el) {
      el.focus({ preventScroll: true });
    }
  });
}

/**
 * Compute fixed dropdown geometry that stays visible when the virtual keyboard is open.
 * @returns {{ top: number, left: number, width: number, maxHeight: number, placement: 'above'|'below' } | null}
 */
export function measureSearchDropdownPosition(anchorEl, { maxPreferred = 280, minHeight = 120 } = {}) {
  if (!anchorEl) return null;

  const rect = anchorEl.getBoundingClientRect();
  const vv = window.visualViewport;
  const viewportTop = vv?.offsetTop ?? 0;
  const viewportHeight = vv?.height ?? window.innerHeight;
  const viewportBottom = viewportTop + viewportHeight;

  const spaceBelow = viewportBottom - rect.bottom - 8;
  const spaceAbove = rect.top - viewportTop - 8;
  const coarse = isCoarsePointer();

  let placement;
  if (coarse && spaceBelow < maxPreferred && spaceAbove >= minHeight) {
    placement = 'above';
  } else if (spaceBelow >= minHeight || spaceBelow >= spaceAbove) {
    placement = 'below';
  } else {
    placement = 'above';
  }

  const available = placement === 'below' ? spaceBelow : spaceAbove;
  const maxHeight = Math.min(maxPreferred, Math.max(minHeight, available));

  return {
    top: placement === 'below' ? rect.bottom + 4 : rect.top - 4,
    left: rect.left,
    width: rect.width,
    maxHeight,
    placement,
  };
}

/**
 * Max height for a scrollable list sitting directly under a search field (modals, panels).
 * @returns {number | null}
 */
export function measureScrollListMaxHeight(anchorEl, { minHeight = 80, padding = 12, maxPreferred = 480 } = {}) {
  if (!anchorEl) return null;

  const rect = anchorEl.getBoundingClientRect();
  const vv = window.visualViewport;
  const viewportBottom = (vv?.offsetTop ?? 0) + (vv?.height ?? window.innerHeight);
  const available = viewportBottom - rect.bottom - padding;

  return Math.min(maxPreferred, Math.max(minHeight, available));
}

/** Scroll filter inputs into view on touch devices when the keyboard opens. */
export function scrollSearchInputIntoView(inputRef) {
  if (typeof window === 'undefined' || !isCoarsePointer()) return;
  const el = inputRef?.current;
  if (!el) return;

  requestAnimationFrame(() => {
    el.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  });
}

/**
 * Reset document scroll before / as the mobile keyboard opens so fixed sheets stay aligned.
 * Call on search focus inside bottom sheets.
 */
export function stabilizeMobileSearchViewport() {
  if (typeof window === 'undefined' || !isMobileSearchSheet()) return;

  const fixScroll = () => {
    const vv = window.visualViewport;
    const y = vv?.offsetTop > 0 ? vv.offsetTop : 0;
    window.scrollTo(0, y);
  };

  fixScroll();
  requestAnimationFrame(fixScroll);
  requestAnimationFrame(() => requestAnimationFrame(fixScroll));
  window.setTimeout(fixScroll, 80);
  window.setTimeout(fixScroll, 280);
}

/** Prevent input blur before tap registers on dropdown rows (mobile combobox pattern). */
export function preventInputBlurOnPointerDown(event) {
  event.preventDefault();
}

/**
 * Blur a search input without scrolling the page (call before closing mobile sheets).
 */
export function blurMobileSearchInput(inputRef) {
  const el = inputRef?.current;
  if (!el || document.activeElement !== el) return;
  el.blur();
}

/**
 * After the virtual keyboard closes, iOS Safari can leave the visual viewport scaled or
 * shifted. Briefly nudge the viewport meta so scale resets without blocking user zoom long-term.
 * Fixed delays cover the common case; also re-nudges once the visual viewport actually grows
 * back (keyboard finished closing) in case the close animation runs long on some devices.
 */
export function nudgeMobileViewportScaleAfterKeyboard() {
  if (typeof window === 'undefined' || !isMobileSearchSheet()) return;

  const meta = document.querySelector('meta[name="viewport"]');
  if (!meta) return;

  const original = meta.getAttribute('content') ?? '';

  const nudge = () => {
    meta.setAttribute('content', 'width=device-width, initial-scale=1, maximum-scale=1');
    // Force layout reflow before restoring so iOS reapplies the layout viewport scale.
    void document.documentElement.offsetHeight;
    meta.setAttribute('content', original);
  };

  const vv = window.visualViewport;
  const startHeight = vv?.height ?? 0;
  const onViewportGrow = () => {
    if (vv.height > startHeight + 40) {
      vv.removeEventListener('resize', onViewportGrow);
      nudge();
    }
  };
  vv?.addEventListener('resize', onViewportGrow);
  window.setTimeout(() => vv?.removeEventListener('resize', onViewportGrow), 1000);

  requestAnimationFrame(nudge);
  window.setTimeout(nudge, 150);
  window.setTimeout(nudge, 400);
}
